import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAnalytics } from '../../hooks/report/useAnalytics';
import { useCustomReport } from '../../hooks/report/useCustomReport';
import { useReportState } from '../../hooks/report/useReportState';
import { ReportStep } from '../../hooks/report/types';
import { reportStepsAPI } from '../../services/api';
import { validateTaskId, getTaskSelection, saveTaskSelection } from '../../utils/taskIdValidator';
import { refreshAccessTokenForSSE } from '../../utils/sseTokenRefresh';
import { SseReconnectPolicy } from '../../utils/sseReconnect';
import FileInfoPanel from './FileInfoPanel';
import FloatingFooter from './FloatingFooter';
import BackButton from './BackButton';
import AddReportCard from './AddReportCard';
import { useTranslation } from 'react-i18next';
import './AnalyticsStep.css';

const AnalyticsStep: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { t } = useTranslation('create');
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const { generateAnalytics, loadAnalyticsData, reports: analyticsReports } = useAnalytics();
  const { generateCustomReport } = useCustomReport();
  const { 
    state: { 
      taskId, 
      analyticsData, 
      reports,
      isLoading, 
      error 
    },
    setSelectedReportIndices,
    setReports,
    setError,
    setTaskId
  } = useReportState();
  
  // State for file information
  const [fileInfo, setFileInfo] = useState<any>(null);
  const [isPro, setIsPro] = useState<boolean>(false);
  
  // Состояние для отслеживания загрузки информации о файле
  const [fileInfoLoaded, setFileInfoLoaded] = useState<boolean>(false);
  
  // Состояние для хранения выбранных отчетов
  const [selectedReports, setSelectedReports] = useState<number[]>([]);
  
  // Состояние для отслеживания попытки генерации аналитики
  const [analyticsGenerated, setAnalyticsGenerated] = useState<boolean>(false);
  
  // Ref для отслеживания процесса загрузки/генерации аналитики
  const analyticsProcessStarted = useRef<boolean>(false);
  
  // Streaming state for skeleton placeholders
  const [streamingReports, setStreamingReports] = useState<any[]>([]);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [skeletonCount, setSkeletonCount] = useState<number>(4);
  const prevStreamCountRef = useRef<number>(0);
  
  // Состояние предобработки данных
  const [isPreprocessing, setIsPreprocessing] = useState<boolean>(false);

  // Состояние для отображения формы добавления отчета
  const [showAddReportForm, setShowAddReportForm] = useState<boolean>(false);
  
  // Состояние для хранения пользовательского запроса
  const [customReportQuery, setCustomReportQuery] = useState<string>('');

  // Состояние для раскрытия query_suggestion
  const [expandedSuggestions, setExpandedSuggestions] = useState<{[key: number]: boolean}>({});

  // Модальное окно рекомендации Pro-режима для больших файлов
  const [showProSuggestionModal, setShowProSuggestionModal] = useState<boolean>(false);
  
  // Используем отчеты из useAnalytics вместо useReportState
  const reportsToRender = analyticsReports && analyticsReports.length > 0 ? analyticsReports : reports;
  
  
  // Получаем taskId из location.state
  const location = useLocation();
  
  // Проверяем наличие taskId и перенаправляем на шаг загрузки, если его нет
  // Загружаем информацию о файле только если она еще не загружена
  useEffect(() => {
    // Проверяем наличие taskId в разных источниках
    const locationTaskId = location.state?.taskId;
    const { taskId: storageTaskId } = getTaskSelection();
    
    console.log('AnalyticsStep useEffect - проверка taskId:', { 
      locationTaskId, 
      storageTaskId, 
      stateTaskId: taskId 
    });
    
    // Приоритет: 1) location.state, 2) localStorage, 3) state
    const currentTaskId = locationTaskId || storageTaskId;
    
    if (!taskId && !currentTaskId) {
      // Если нет taskId ни в одном из источников, перенаправляем на шаг загрузки
      console.log('AnalyticsStep - нет taskId, перенаправляем на шаг загрузки');
      navigate(`/create-report/upload`);
      return;
    }
    
    // Если taskId есть в location.state или localStorage, но нет в состоянии, устанавливаем его в состояние
    if (!taskId && currentTaskId) {
      console.log('AnalyticsStep - устанавливаем taskId в состояние:', currentTaskId);
      setTaskId(currentTaskId);
      
      // Сохраняем в localStorage для надежности
      saveTaskSelection(currentTaskId);
    }
    
    // Загружаем информацию о файле только если она еще не загружена
    if (!fileInfoLoaded && !fileInfo) {
      const fetchFileInfo = async () => {
        try {
          console.log('AnalyticsStep - загружаем информацию о файле');
          const taskIdToUse = currentTaskId || taskId;
          if (taskIdToUse) {
            const response = await reportStepsAPI.getTaskStatus(taskIdToUse);
            console.log('fetchFileInfo - API response:', response.data);
            
            // Extract is_pro from task data
            const isProMode = !!response.data?.data?.is_pro;
            if (isProMode) {
              setIsPro(true);
            }

            if (response.data && response.data.data && response.data.data.file_info) {
              const info = {
                name: response.data.data.file_name,
                ...response.data.data.file_info
              };
              setFileInfo(info);
              setFileInfoLoaded(true);
              console.log('AnalyticsStep - информация о файле успешно загружена');
              // Показываем модал если файл > 100KB и не PRO режим
              if (!isProMode && info.file_size && info.file_size > 100000) {
                setShowProSuggestionModal(true);
              }
            } else if (response.data && response.data.data && response.data.data.excel_data && response.data.data.excel_data.file_info) {
              const info = {
                name: response.data.data.file_name,
                ...response.data.data.excel_data.file_info
              };
              setFileInfo(info);
              setFileInfoLoaded(true);
              console.log('AnalyticsStep - информация о файле успешно загружена');
              if (!isProMode && info.file_size && info.file_size > 100000) {
                setShowProSuggestionModal(true);
              }
            }
          }
        } catch (error) {
          console.error('Ошибка при загрузке информации о файле:', error);
        }
      };
      
      fetchFileInfo();
    }
  }, [taskId, navigate, fileInfo, fileInfoLoaded]);
  
  // Отдельный эффект для загрузки аналитики
  useEffect(() => {
    // Загружаем или генерируем аналитику при монтировании компонента только один раз
    if (!analyticsGenerated && !analyticsProcessStarted.current) {
      analyticsProcessStarted.current = true; // Устанавливаем флаг, что процесс начат
      console.log('[ANALYTICS_STEP] начинаем процесс загрузки/генерации аналитики');
      
      const loadOrGenerateData = async () => {
        try {
          console.log('[ANALYTICS_STEP] пытаемся загрузить аналитические данные из базы данных');
          
          // Получаем taskId из разных источников
          const locationTaskId = location.state?.taskId;
          const { taskId: storageTaskId } = getTaskSelection();
          
          // Приоритет: 1) location.state, 2) localStorage, 3) state
          const currentTaskId = locationTaskId || storageTaskId || taskId;
          
          console.log('[ANALYTICS_STEP] loadOrGenerateData - используем taskId:', currentTaskId);
          console.log('[ANALYTICS_STEP] loadOrGenerateData - текущее состояние:', {
            analyticsGenerated,
            analyticsProcessStarted: analyticsProcessStarted.current,
            isLoading,
            error,
            hasAnalyticsData: !!analyticsData,
            hasReports: !!reports && reports.length > 0,
            hasAnalyticsReports: !!analyticsReports && analyticsReports.length > 0
          });
          
          if (currentTaskId) {
            // Сохраняем taskId во все хранилища для надежности
            console.log('[ANALYTICS_STEP] loadOrGenerateData - сохраняем taskId во все хранилища:', currentTaskId);
            saveTaskSelection(currentTaskId);
          }
          
          // Используем существующий client_id или генерируем новый
          if (!sseClientIdRef.current) {
            sseClientIdRef.current = generateClientId();
            console.log('[ANALYTICS_STEP] loadOrGenerateData - сгенерирован client_id для SSE:', sseClientIdRef.current);
          } else {
            console.log('[ANALYTICS_STEP] loadOrGenerateData - используем существующий client_id для SSE:', sseClientIdRef.current);
          }
          
          // Пытаемся загрузить аналитические данные из базы данных, передавая taskId из location.state
          console.log('[ANALYTICS_STEP] loadOrGenerateData - вызываем loadAnalyticsData с taskId:', currentTaskId);
          const dataLoaded = await loadAnalyticsData(currentTaskId);
          console.log('[ANALYTICS_STEP] loadOrGenerateData - результат loadAnalyticsData:', dataLoaded);
          
          // Проверяем, что загруженные данные соответствуют текущему taskId
          if (dataLoaded && analyticsData) {
            console.log('[ANALYTICS_STEP] loadOrGenerateData - данные загружены, проверяем соответствие taskId');
            
            if (!analyticsData.task_id) {
              console.warn('[ANALYTICS_STEP] loadOrGenerateData - в загруженных данных отсутствует task_id');
              
              // Если данные загружены, но в них нет task_id, проверяем другие признаки несоответствия
              if (currentTaskId && analyticsData && !validateTaskId(analyticsData, currentTaskId)) {
                console.error(`[ANALYTICS_STEP] loadOrGenerateData - несоответствие taskId в загруженных данных: ожидался ${currentTaskId}`);
                // Принудительно генерируем новые данные
                console.log('[ANALYTICS_STEP] loadOrGenerateData - принудительно генерируем новые данные из-за несоответствия taskId');
                setIsStreaming(true);
                await generateAnalytics(sseClientIdRef.current);
              }
            } else if (currentTaskId && analyticsData.task_id !== currentTaskId) {
              console.error(`[ANALYTICS_STEP] loadOrGenerateData - несоответствие taskId в загруженных данных: ожидался ${currentTaskId}, получен ${analyticsData.task_id}`);
              // Принудительно генерируем новые данные
              console.log('[ANALYTICS_STEP] loadOrGenerateData - принудительно генерируем новые данные из-за несоответствия taskId');
              setIsStreaming(true);
              await generateAnalytics(sseClientIdRef.current);
            } else {
              console.log('[ANALYTICS_STEP] loadOrGenerateData - аналитические данные успешно загружены из базы данных');
              console.log('[ANALYTICS_STEP] loadOrGenerateData - количество отчетов:', analyticsData.reports?.length || 0);
            }
          } else if (!dataLoaded) {
            console.log('[ANALYTICS_STEP] loadOrGenerateData - данные не найдены в базе данных, генерируем аналитику');
            setIsStreaming(true);
            await generateAnalytics(sseClientIdRef.current);
            // НЕ устанавливаем analyticsGenerated = true здесь!
            // Это будет установлено в SSE обработчике когда получим is_final: true
            console.log('[ANALYTICS_STEP] loadOrGenerateData - генерация аналитики запущена, ждем SSE событий');
          } else {
            // Данные уже загружены из БД
            console.log('[ANALYTICS_STEP] loadOrGenerateData - устанавливаем analyticsGenerated = true (данные из БД)');
            setAnalyticsGenerated(true);
          }
        } catch (error: any) {
          console.error('[ANALYTICS_STEP] Ошибка при загрузке/генерации аналитики:', error);
          console.error('[ANALYTICS_STEP] Детали ошибки:', error.response?.data || error.message);
        }
      };
      
      loadOrGenerateData();
    }
  }, [generateAnalytics, loadAnalyticsData, analyticsGenerated, analyticsData]);
  
  // Ref для хранения client_id для SSE соединения
  const sseClientIdRef = useRef<string>('');
  
  // Set up SSE connection for real-time updates
  useEffect(() => {
    // Получаем текущий taskId из localStorage
    const { taskId: currentTaskId } = getTaskSelection();

    if (!currentTaskId) {
      console.log('[ANALYTICS_STEP] SSE - taskId не найден в localStorage');
      return;
    }

    // Используем существующий client_id или генерируем новый
    if (!sseClientIdRef.current) {
      sseClientIdRef.current = generateClientId();
      console.log('[ANALYTICS_STEP] SSE - сгенерирован client_id для SSE:', sseClientIdRef.current);
    } else {
      console.log('[ANALYTICS_STEP] SSE - используем существующий client_id для SSE:', sseClientIdRef.current);
    }

    // Tracks the currently live EventSource so cleanup/reconnect
    // always close the right one after a token-refresh swap.
    let activeEventSource: EventSource | null = null;
    let refreshAttempted = false;

    // Exponential-backoff reconnect policy: attempts counter only resets
    // after 10s of a stable (uninterrupted) connection. Without this, an
    // onopen that fires immediately (server returns 200 instantly) would
    // reset the counter every open→error cycle, producing a ~300ms
    // reconnect loop in production.
    const policy = new SseReconnectPolicy();
    // Re-arm the one-shot refresh flag only once the connection has proven
    // stable for 10s — so a later, unrelated token expiration can still
    // trigger a refresh, while a flapping connection doesn't re-open the
    // refresh gate on every spurious open.
    policy.onStable = () => { refreshAttempted = false; };
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
      if (reconnectTimer) return; // не дублировать таймер
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connectSSE(); },
                                  policy.nextDelayMs());
    };

    const connectSSE = () => {
    // Get the authentication token
    const token = localStorage.getItem('token');

    // Create SSE connection using the client ID and include the token as a query parameter
    const eventSource = new EventSource(`/api/v1/sse/connect/${sseClientIdRef.current}?token=${token}`);
    activeEventSource = eventSource;

    console.log('[ANALYTICS_STEP] SSE - Setting up SSE connection with client ID:', sseClientIdRef.current);

    // Handle connection open
    eventSource.onopen = () => {
      console.log('[ANALYTICS_STEP] SSE - SSE connection opened');
      // refreshAttempted is no longer reset here directly — policy.onOpen()
      // arms a 10s stable-timer that resets both the backoff counter and
      // (via onStable, wired above) refreshAttempted, but only if the
      // connection survives that long without another error.
      policy.onOpen();
    };

    // Handle connection error — try a one-shot token refresh before giving up.
    // Native EventSource hides the HTTP status (403 on expired token),
    // so we blind-refresh once and rebuild the connection.
    eventSource.onerror = async (error) => {
      console.error('[ANALYTICS_STEP] SSE - SSE connection error:', error);
      policy.onErrorOrClose();

      if (refreshAttempted) return;
      refreshAttempted = true;

      eventSource.close();
      if (activeEventSource === eventSource) {
        activeEventSource = null;
      }

      const newToken = await refreshAccessTokenForSSE();
      if (newToken) {
        console.log('[ANALYTICS_STEP] SSE - Token refreshed after error, reconnecting');
        scheduleReconnect();
      } else {
        console.warn('[ANALYTICS_STEP] SSE - Token refresh failed, giving up');
      }
    };
    
    // Handle data preprocessing events
    eventSource.addEventListener('data_preprocessing_start', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[ANALYTICS_STEP] SSE - Data preprocessing started:', data);
        setIsPreprocessing(true);
      } catch (error) {
        console.error('[ANALYTICS_STEP] SSE - Error parsing preprocessing start event:', error);
      }
    });

    eventSource.addEventListener('data_preprocessing_complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[ANALYTICS_STEP] SSE - Data preprocessing complete:', data);
        setIsPreprocessing(false);
      } catch (error) {
        console.error('[ANALYTICS_STEP] SSE - Error parsing preprocessing complete event:', error);
      }
    });

    // Handle analytics partial result events
    eventSource.addEventListener('analytics_partial_result', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[ANALYTICS_STEP] SSE - Received analytics partial result');

        const partialResult = data.partial_result;
        const isFinal = data.is_final;

        // Check if the partial result has a partial_text field
        let processedResult = partialResult;
        if (processedResult && !processedResult.partial_text && typeof processedResult === 'string') {
          processedResult = { partial_text: processedResult };
        }

        // Try to extract reports from the partial result
        try {
          const extractReportsFromText = (text: string) => {
            const reportsMatch = text.match(/"reports"\s*:\s*\[([\s\S]*?)(?:\]\s*,|\](?!\s*,))/);

            if (reportsMatch && reportsMatch[1]) {
              const reportsContent = reportsMatch[1];
              const reportObjects: any[] = [];
              const objectRegex = /{[^{}]*(?:{[^{}]*}[^{}]*)*}/g;
              let match;

              while ((match = objectRegex.exec(reportsContent)) !== null) {
                try {
                  reportObjects.push(JSON.parse(match[0]));
                } catch (parseError) {
                  // skip unparseable objects
                }
              }

              if (reportObjects.length > 0) return reportObjects;
            }

            try {
              const jsonStart = text.indexOf('{');
              const jsonEnd = text.lastIndexOf('}');

              if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                const jsonObj = JSON.parse(text.substring(jsonStart, jsonEnd + 1));
                if (jsonObj.reports && Array.isArray(jsonObj.reports)) return jsonObj.reports;
              }
            } catch (parseError) {
              const reportsStartMatch = text.match(/"reports"\s*:\s*\[/);
              if (reportsStartMatch && reportsStartMatch.index !== undefined) {
                const partialReportsContent = text.substring(reportsStartMatch.index + reportsStartMatch[0].length);
                const objectRegex = /{[^{}]*(?:{[^{}]*}[^{}]*)*}/g;
                const reportObjects: any[] = [];
                let match;

                while ((match = objectRegex.exec(partialReportsContent)) !== null) {
                  try {
                    reportObjects.push(JSON.parse(match[0]));
                  } catch (objParseError) {
                    // skip
                  }
                }

                if (reportObjects.length > 0) return reportObjects;
              }
            }

            return null;
          };

          let extractedReports: any[] | null = null;

          if (typeof processedResult === 'string') {
            extractedReports = extractReportsFromText(processedResult);
          } else if (processedResult && processedResult.partial_text) {
            extractedReports = extractReportsFromText(processedResult.partial_text);
          }

          // Update streaming reports with extracted data
          if (extractedReports && extractedReports.length > 0) {
            console.log('[ANALYTICS_STEP] SSE - Extracted', extractedReports.length, 'reports from partial result');
            setStreamingReports(extractedReports);
            // Keep at least 1 skeleton ahead of extracted data
            setSkeletonCount(prev => Math.max(prev, extractedReports!.length + 1));
          }
        } catch (error) {
          console.error('[ANALYTICS_STEP] SSE - Error extracting reports from partial result:', error);
        }

        // If this is the final result, reload from DB and switch to final state
        if (isFinal) {
          console.log('[ANALYTICS_STEP] SSE - Received final analytics result');
          loadAnalyticsData(currentTaskId).then(() => {
            setIsStreaming(false);
            setStreamingReports([]);
            setAnalyticsGenerated(true);
            console.log('[ANALYTICS_STEP] SSE - Analytics complete, switched to final cards');
          });
        }
      } catch (error) {
        console.error('[ANALYTICS_STEP] SSE - Error parsing analytics partial result:', error);
      }
    });

    // Handle final analytics completion event
    eventSource.addEventListener('claude_analytics_suggests', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[ANALYTICS_STEP] SSE - Received claude_analytics_suggests (final):', data);

        if (data && data.reports) {
          console.log('[ANALYTICS_STEP] SSE - Final analytics data received with', data.reports.length, 'reports');
          loadAnalyticsData(currentTaskId).then(() => {
            setIsStreaming(false);
            setStreamingReports([]);
            setAnalyticsGenerated(true);
            console.log('[ANALYTICS_STEP] SSE - Analytics complete via claude_analytics_suggests');
          });
        }
      } catch (error) {
        console.error('[ANALYTICS_STEP] SSE - Error parsing claude_analytics_suggests:', error);
      }
    });

    };

    connectSSE();

    // Clean up SSE connection on unmount
    return () => {
      console.log('[ANALYTICS_STEP] SSE - Cleaning up SSE connection');
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      policy.onErrorOrClose();
      if (activeEventSource) {
        activeEventSource.close();
        activeEventSource = null;
      }
    };
  }, []);
  
  // Инициализируем список выбранных отчетов при получении отчетов
  useEffect(() => {
    console.log('[ANALYTICS_STEP] состояние отчетов изменилось:', {
      reportsLength: reports?.length || 0,
      analyticsReportsLength: analyticsReports?.length || 0,
      isLoading,
      hasAnalyticsData: !!analyticsData
    });
    
    // Используем reportsToRender вместо reports
    if (reportsToRender && reportsToRender.length > 0) {
      console.log('[ANALYTICS_STEP] получены отчеты для обработки, количество:', reportsToRender.length);
      
      // Логируем статусы всех отчетов
      reportsToRender.forEach(report => {
        console.log(`[ANALYTICS_STEP] отчет '${report.name}' (индекс ${report.index}): статус=${report.status}, удален=${report.deleted}`);
      });
      
      // Build a set of deleted indices from local reports state
      // (analyticsReports from useAnalytics may not have the deleted flag)
      const deletedIndices = new Set(
        reports?.filter((r: any) => r.deleted).map((r: any) => r.index) || []
      );

      // Фильтруем отчеты, исключая те, которые помечены как удаленные
      const selectedIndices = reportsToRender
        .filter(report => !report.deleted && !deletedIndices.has(report.index))
        .map(report => report.index);
      
      console.log('[ANALYTICS_STEP] выбранные индексы отчетов (исключая удаленные):', selectedIndices);
      setSelectedReports(selectedIndices);
      
      // Устанавливаем isLoading в false, чтобы показать реальные карточки
      if (isLoading) {
        console.log('[ANALYTICS_STEP] завершаем загрузку, показываем реальные карточки');
      }
    } else {
      console.log('[ANALYTICS_STEP] отчеты для обработки отсутствуют или пусты');
    }
  }, [reports, analyticsReports, isLoading, reportsToRender, analyticsData]);
  
  // Обработчик удаления карточки
  const handleDeleteCard = async (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Получаем актуальный taskId из localStorage
    const { taskId: currentTaskId } = getTaskSelection();
    
    if (!currentTaskId) {
      console.error('[ANALYTICS_STEP] handleDeleteCard - taskId не найден в localStorage');
      return;
    }
    
    try {
      // Используем индекс напрямую, без добавления 1
      // Индексы отчетов в backend уже содержат правильные значения
      
      // Подробное логирование для отладки
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Удаление отчета с индексом ${index}, тип: ${typeof index}`);
      const reportToDelete = reportsToRender.find(r => r.index === index);
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Полный объект отчета:`, reportToDelete);
      
      // Вызываем API для обновления статуса отчета
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Отправка запроса на удаление отчета с индексом ${index}`);
      await reportStepsAPI.updateReportStatus(currentTaskId, index, true);
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Отчет с индексом ${index} успешно помечен как удаленный`);
      
      // Обновляем локальное состояние
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Обновляем локальное состояние selectedReports, удаляем индекс ${index}`);
      setSelectedReports(prev => {
        const newSelectedReports = prev.filter(idx => idx !== index);
        console.log(`[ANALYTICS_STEP] handleDeleteCard - Новые выбранные индексы:`, newSelectedReports);
        return newSelectedReports;
      });
      
      // Обновляем локальное состояние отчетов вместо полной перезагрузки данных
      console.log(`[ANALYTICS_STEP] handleDeleteCard - Обновляем локальное состояние отчетов`);
      const updatedReports = reports.map((report: ReportStep) => {
        if (report.index === index) {
          console.log(`[ANALYTICS_STEP] handleDeleteCard - Помечаем отчет с индексом ${index} как удаленный`);
          return { ...report, deleted: true };
        }
        return report;
      });
      setReports(updatedReports);
    } catch (error: any) {
      console.error(`[ANALYTICS_STEP] handleDeleteCard - Ошибка при удалении отчета с индексом ${index}:`, error);
      console.error('[ANALYTICS_STEP] handleDeleteCard - Детали ошибки:', error.response?.data || error.message);
    }
  };
  
  // Генерация client_id для SSE соединения
  const generateClientId = () => {
    // Try to get the user ID from the token
    let userId = "anonymous";
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        // JWT tokens are in the format header.payload.signature
        // We need to decode the payload (second part)
        const payload = token.split('.')[1];
        // Decode the base64 payload
        const decodedPayload = JSON.parse(atob(payload));
        // Get the user ID from the sub claim
        if (decodedPayload.sub) {
          userId = decodedPayload.sub;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
    
    return `user_${userId}_${Math.random().toString(36).substring(2, 9)}`;
  };
  
  // Обработчик перехода к следующему шагу
  const handleContinue = async () => {
    console.log('[ANALYTICS_STEP] handleContinue - сохраняем выбранные отчеты:', selectedReports);
    
    // Получаем текущий taskId из localStorage
    const { taskId: currentTaskId } = getTaskSelection();
    
    if (!currentTaskId) {
      console.error('[ANALYTICS_STEP] handleContinue - taskId не найден в localStorage');
      setError(t('processing.error_task_not_init'));
      return;
    }
    
    try {
      // Сохраняем taskId в localStorage
      console.log('[ANALYTICS_STEP] handleContinue - сохраняем taskId в localStorage:', currentTaskId);
      saveTaskSelection(currentTaskId);
      
      // Сохраняем выбранные отчеты в хуке
      console.log('[ANALYTICS_STEP] handleContinue - сохраняем выбранные отчеты в хуке:', selectedReports);
      setSelectedReportIndices(selectedReports);
      
      // Сохраняем выбранные отчеты в localStorage для надежности
      console.log('[ANALYTICS_STEP] handleContinue - сохраняем выбранные отчеты в localStorage:', selectedReports);
      localStorage.setItem('selected_report_indices', JSON.stringify(selectedReports));
      
      // Используем существующий client_id от SSE соединения
      const clientId = sseClientIdRef.current;
      console.log('[ANALYTICS_STEP] handleContinue - используем существующий client_id для SSE:', clientId);
      
      // Сохраняем client_id в localStorage для использования в ProcessingStep
      console.log('[ANALYTICS_STEP] handleContinue - сохраняем client_id в localStorage:', clientId);
      localStorage.setItem('report_processing_client_id', clientId);

      // Запускаем обработку всех отчетов на бэкенде (fire-and-forget, не ждём завершения)
      console.log('[ANALYTICS_STEP] handleContinue - запускаем обработку всех отчетов на бэкенде');
      reportStepsAPI.processAllReports(currentTaskId, clientId).catch(error => {
        console.error('[ANALYTICS_STEP] handleContinue - Ошибка обработки отчетов на бэкенде:', error.response?.data || error.message);
      });

      // Сразу переходим к шагу обработки (не ждём завершения на бэкенде)
      console.log('[ANALYTICS_STEP] handleContinue - переходим к шагу обработки');
      navigate(`/create-report/processing`, { state: { clientId } });
    } catch (error: any) {
      console.error('[ANALYTICS_STEP] handleContinue - Ошибка при запуске обработки отчетов:', error);
      console.error('[ANALYTICS_STEP] handleContinue - Детали ошибки:', error.response?.data || error.message);
      setError(t('processing.error_task_not_init'));
    }
  };
  
  // Обработчик генерации пользовательского отчета
  const handleGenerateCustomReport = async () => {
    if (!customReportQuery.trim()) return;
    
    try {
      // Сначала скрываем форму ввода и сбрасываем запрос
      // чтобы пользователь сразу увидел плейсхолдер
      const query = customReportQuery;
      setCustomReportQuery('');
      setShowAddReportForm(false);
      
      // Затем вызываем метод для генерации пользовательского отчета
      // Pass existing SSE client_id so events go to the right channel
      await generateCustomReport(query, sseClientIdRef.current);
    } catch (error: any) {
      console.error('Ошибка при генерации пользовательского отчета:', error);
      console.error('Детали ошибки:', error.response?.data || error.message);
    }
  };
  
  // Track fade-in for newly appeared streaming cards
  useEffect(() => {
    prevStreamCountRef.current = streamingReports.length;
  }, [streamingReports]);

  // Skeleton card placeholder
  const renderSkeletonCard = (index: number) => (
    <div key={`skeleton-${index}`} className="analytics-card skeleton-card">
      <div className="shimmer-bar title" />
      <div className="shimmer-bar query" />
      <div className="sheets-info">
        <div className="shimmer-bar section-header" />
        <div className="shimmer-bar list-item" />
        <div className="shimmer-bar list-item" style={{ width: '50%' }} />
      </div>
      <div className="processing-steps">
        <div className="shimmer-bar section-header" />
        <div className="shimmer-bar list-item" />
        <div className="shimmer-bar list-item" style={{ width: '55%' }} />
        <div className="shimmer-bar list-item" style={{ width: '45%' }} />
      </div>
      <div className="shimmer-bar button" />
    </div>
  );

  // Рендер карточек аналитики
  const renderAnalyticsCards = () => {
    // --- STREAMING STATE: show real cards + remaining skeletons ---
    if (isStreaming) {
      const totalSlots = Math.max(skeletonCount, streamingReports.length);
      return (
        <div className="analytics-cards">
          {Array.from({ length: totalSlots }, (_, i) => {
            if (i < streamingReports.length) {
              const report = streamingReports[i];
              const isNew = i >= prevStreamCountRef.current;
              return (
                <div key={`stream-${report.index ?? i}`}
                     className={`analytics-card${isNew ? ' card-fade-in' : ''}`}>
                  <h4>{report.name}</h4>
                  {report.query_suggestion && (
                    <p
                      className={`query-suggestion${expandedSuggestions[report.index ?? i] ? ' expanded' : ''}`}
                      onClick={() => setExpandedSuggestions(prev => ({ ...prev, [report.index ?? i]: !prev[report.index ?? i] }))}
                    >
                      {report.query_suggestion}
                    </p>
                  )}
                  {report.sheets && (
                    <div className="sheets-info">
                      <h5>{t('analytics_step.sheets_title')}</h5>
                      <ul>{report.sheets.map((s: string, j: number) => <li key={j}>{s}</li>)}</ul>
                    </div>
                  )}
                  {report.processing_steps && (
                    <div className="processing-steps">
                      <h5>{t('analytics_step.steps_title')}</h5>
                      <ul>{report.processing_steps.map((s: string, j: number) => <li key={j}>{s}</li>)}</ul>
                    </div>
                  )}
                </div>
              );
            } else {
              return renderSkeletonCard(i);
            }
          })}
        </div>
      );
    }

    // --- LOADING / INITIAL STATE: show skeleton placeholders ---
    if (isLoading || !reportsToRender || !Array.isArray(reportsToRender) || reportsToRender.length === 0) {
      return (
        <div className="analytics-cards">
          {Array.from({ length: skeletonCount }, (_, i) => renderSkeletonCard(i))}
        </div>
      );
    }

    // --- FINAL STATE: show real cards with actions ---
    return (
      <div className="analytics-cards">
        {reportsToRender.map((report: any) => {
          const reportIndex = report.index;
          const isSelected = selectedReports.includes(reportIndex);

          // Custom report placeholder (generating)
          if (report.isPlaceholder) {
            return (
              <div
                key={`placeholder-report-${reportIndex}`}
                className="analytics-card add-report-placeholder loading"
              >
                <h4>{report.name}</h4>
                {report.query_suggestion && (
                  <p className="query-suggestion">{t('analytics_step.query_label', { query: report.query_suggestion })}</p>
                )}
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
                <p>{t('analytics_step.placeholder_wait')}</p>
              </div>
            );
          }

          return (
            <div
              key={`report-${reportIndex}`}
              className={`analytics-card${isSelected ? '' : ' deleted'}`}
            >
              <h4>{report.name}</h4>
              {report.query_suggestion && (
                <p
                  className={`query-suggestion${expandedSuggestions[reportIndex] ? ' expanded' : ''}`}
                  onClick={() => setExpandedSuggestions(prev => ({ ...prev, [reportIndex]: !prev[reportIndex] }))}
                >
                  {report.query_suggestion}
                </p>
              )}
              {report.sheets && (
                <div className="sheets-info">
                  <h5>{t('analytics_step.sheets_title')}</h5>
                  <ul>
                    {report.sheets.map((sheet: string, sheetIdx: number) => (
                      <li key={sheetIdx}>{sheet}</li>
                    ))}
                  </ul>
                </div>
              )}
              {report.processing_steps && (
                <div className="processing-steps">
                  <h5>{t('analytics_step.steps_title')}</h5>
                  <ul>
                    {report.processing_steps.map((step: string, stepIdx: number) => (
                      <li key={stepIdx}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="card-actions">
                {isSelected ? (
                  <button
                    className="delete-button"
                    onClick={(e) => handleDeleteCard(reportIndex, e)}
                  >
                    {t('analytics_step.card_delete')}
                  </button>
                ) : (
                  <button
                    className="restore-button"
                    onClick={async (e) => {
                      e.stopPropagation();

                      const { taskId: currentTaskId } = getTaskSelection();

                      if (!currentTaskId) {
                        console.error('handleRestoreCard - taskId не найден в localStorage');
                        return;
                      }

                      try {
                        await reportStepsAPI.updateReportStatus(currentTaskId, reportIndex, false);

                        setSelectedReports(prev => [...prev, reportIndex]);

                        const updatedReports = reports.map((r: ReportStep) =>
                          r.index === reportIndex
                            ? { ...r, deleted: false }
                            : r
                        );
                        setReports(updatedReports);
                      } catch (error: any) {
                        console.error(`Ошибка при восстановлении отчета с индексом ${reportIndex}:`, error);
                      }
                    }}
                  >
                    {t('analytics_step.card_restore')}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <AddReportCard
          showForm={showAddReportForm}
          customReportQuery={customReportQuery}
          onQueryChange={setCustomReportQuery}
          onCancel={() => {
            setShowAddReportForm(false);
            setCustomReportQuery('');
          }}
          onGenerate={handleGenerateCustomReport}
          onAddClick={() => setShowAddReportForm(true)}
        />
      </div>
    );
  };

  const fileSizeKb = fileInfo?.file_size ? Math.round(fileInfo.file_size / 1024) : 0;

  return (
    <div className="analytics-step">
      <BackButton to="/create-report/upload" />

      {/* Модальное окно рекомендации PRO-режима */}
      {showProSuggestionModal && (
        <div className="pro-suggestion-overlay" onClick={() => setShowProSuggestionModal(false)}>
          <div className="pro-suggestion-modal" onClick={e => e.stopPropagation()}>
            <div className="pro-suggestion-header">
              <span className="pro-suggestion-icon">&#9888;</span>
              <h3>{t('analytics_step.pro_modal_title', { size: fileSizeKb })}</h3>
            </div>
            <p className="pro-suggestion-desc">
              {t('analytics_step.pro_modal_desc')}
            </p>
            <div className="pro-suggestion-features">
              <h4><span className="pro-badge">PRO</span> {t('analytics_step.pro_modal_features_title')}</h4>
              <ul>
                <li>{t('analytics_step.pro_modal_f1')}</li>
                <li>{t('analytics_step.pro_modal_f2')}</li>
                <li>{t('analytics_step.pro_modal_f3')}</li>
                <li>{t('analytics_step.pro_modal_f4')}</li>
              </ul>
              <p className="pro-suggestion-cost">{t('analytics_step.pro_modal_cost')}</p>
            </div>
            <div className="pro-suggestion-actions">
              <button
                className="pro-suggestion-switch"
                onClick={async () => {
                  setShowProSuggestionModal(false);
                  const { taskId: currentTaskId } = getTaskSelection();
                  if (!currentTaskId) return;
                  try {
                    await reportStepsAPI.setTaskProMode(currentTaskId, true);
                    setIsPro(true);
                    // Сбрасываем визуальное состояние, но НЕ analyticsProcessStarted
                    // чтобы useEffect не запустил вторую генерацию параллельно
                    setStreamingReports([]);
                    setIsStreaming(true);
                    await generateAnalytics(sseClientIdRef.current);
                  } catch (err) {
                    console.error('[ANALYTICS_STEP] Ошибка переключения PRO-режима:', err);
                  }
                }}
              >
                {t('analytics_step.pro_modal_switch')}
              </button>
              <button
                className="pro-suggestion-continue"
                onClick={() => setShowProSuggestionModal(false)}
              >
                {t('analytics_step.pro_modal_continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isPro && (
        <div className="pro-mode-banner">
          <span className="pro-badge">PRO</span>
          <span>{t('analytics_step.pro_banner')}</span>
        </div>
      )}

      {fileInfo && <FileInfoPanel fileInfo={fileInfo} taskId={taskId || getTaskSelection().taskId || undefined} />}

      {isPreprocessing && (
        <div className="preprocessing-indicator" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          margin: '0 0 16px 0',
          backgroundColor: 'rgba(74, 144, 226, 0.08)',
          borderRadius: '8px',
          border: '1px solid rgba(74, 144, 226, 0.2)',
          fontSize: '14px',
          color: '#4a90e2'
        }}>
          <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }}></div>
          {t('analytics_step.preprocessing')}
        </div>
      )}

      {renderAnalyticsCards()}

      {/* Summary only when final cards are shown */}
      {!isLoading && !isStreaming && reportsToRender && reportsToRender.length > 0 && (
        <div className="analytics-summary">
          <p>{t('analytics_step.selected_summary', { selected: selectedReports.length, total: reportsToRender.length })}</p>
        </div>
      )}

      {error && (
        <div className="error-alert">
          <h3>{t('analytics_step.error_title')}</h3>
          <p>{error}</p>
        </div>
      )}

      {/* Floating footer only when final cards are shown */}
      {!isLoading && !isStreaming && reportsToRender && reportsToRender.length > 0 && (
        <FloatingFooter
          selectedCount={selectedReports.length}
          totalCount={reportsToRender.length}
          onGenerate={handleContinue}
          disabled={selectedReports.length === 0}
          isPro={isPro}
        />
      )}
    </div>
  );
};

export default AnalyticsStep;
